import { BookmarkApplication } from '@/application/bookmark/bookmark';
import type { TBookmarkFetchOptions } from '@/application/bookmark/bookmark.types';
import { FeedApplication } from '@/application/feed/feed';
import { MuteApplication } from '@/application/mute/mute';
import { SettingsApplication } from '@/application/settings/settings';
import { Logger } from '@/libs/logger/logger';
import type { Pubky } from '@/models/models.types';
import type { SettingsState } from '@/stores/settings/settings.types';

/**
 * MigrationApplication orchestrates post-DB-recreation re-sync of critical homeserver data.
 *
 * ADR-0009: MigrationApplication is a privileged root-node orchestrator (like BootstrapApplication).
 * It is a source node (in-degree 0) that fans out to multiple domain Applications for
 * post-migration data re-sync. No other Application class may call MigrationApplication.
 */
export class MigrationApplication {
  private constructor() {}

  /**
   * Fetches critical homeserver data that is not automatically re-fetched after DB recreation.
   * Calls MuteApplication, FeedApplication, SettingsApplication and BookmarkApplication in parallel.
   *
   * @param isCurrent - Whether the account the resync started for is still the current one; the
   *   bookmarks reconcile checks it before writing.
   * @returns Remote settings if available (for Controller to apply to Zustand store)
   */
  static async resync(
    pubky: Pubky,
    localSettings: SettingsState,
    { isCurrent }: TBookmarkFetchOptions = {},
  ): Promise<SettingsState | null> {
    Logger.info('Starting post-DB-recreation re-sync', { pubky });

    // ADR-0009: MigrationApplication → leaf Applications (depth 1)
    const [, , remoteSettingsResult] = await Promise.allSettled([
      MuteApplication.fetchMutedUsers(pubky),
      FeedApplication.fetchFeeds(pubky),
      SettingsApplication.initializeSettings(pubky, localSettings),
      // Private and account-scoped: the guard keeps an outlived fetch from writing another account's rows.
      BookmarkApplication.fetchBookmarks(pubky, { isCurrent }),
    ]);

    Logger.info('Post-DB-recreation re-sync completed', { pubky });

    if (remoteSettingsResult.status === 'rejected') {
      return null;
    }

    return remoteSettingsResult.value;
  }
}
